import { useEffect, useRef } from 'react';

import type { AssignWizardHandle, CustomTestDetail, CustomTestLanguage, QuestionTree, QuestionTreeResult } from 'pages/custom_tests';
import { useCustomTestArchiveDialogs } from 'pages/custom_tests/hooks/useCustomTestArchiveDialogs';
import { CustomTestEditContext } from 'pages/custom_tests/components/custom_test_edit/context';
import { AssignWizard } from 'pages/custom_tests/components/assign_wizard/AssignWizard';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Tabs from 'pages/custom_tests/components/custom_test_edit/tabs';
import { TruncatedText } from 'components/truncated_text/TruncatedText';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { Alert, Badge, Breadcrumb, Modal } from 'react-bootstrap';
import { CUSTOM_TESTS_V2_BASE } from 'pages/custom_tests/types';
import { Dialog, DialogHandle } from 'components/modal_manager';
import { useCustomTests } from 'pages/custom_tests/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';
import { LanguagesApi } from 'apis/LanguagesApi';

import 'pages/custom_tests/components/custom_test_edit/styles/CustomTestEdit.scss';

import { useSuperState } from 'hooks';

enum ETabs {
    SETTINGS,
    QUESTIONS,
    ASSIGN
}

enum EMode {
    TRAINING = 1,
    TESTING,
    BOTH
}

export type CustomTestEditContextValue = {
    name: string;
    assignmentRefreshKey: number;
    categoryId: number;
    confirmCopy: () => void;
    createdBy?: string;
    createdDate?: string;
    editable: boolean;
    handleQuestionsChange: (questionIds: string, totalSelected: number) => void;
    hasApplicationQuestions?: boolean;
    hasResults: boolean;
    isActive: boolean;
    isEditMode?: boolean;
    isOwner: boolean;
    languageId?: number;
    languages?: { Id: number; Name: string }[];
    loadingQuestions: boolean;
    minScore: number;
    minutes: number;
    modeId: EMode;
    nameValidationError?: string;
    owner?: string;
    productIds: number[];
    questionOrderMode: number;
    questionsError: boolean;
    questionTree: QuestionTree;
    randomly: number;
    refreshAssignments: () => void;
    retryQuestions: () => void;
    selectedQuestionIds: string;
    setProviderState: (state: any) => void;
    testId: number;
    totalAssignmentCount?: number;
    totalQuestions?: number;
    useRandomly: boolean;
};

export const CustomTestEdit = props => {

    const [state, setState] = useSuperState({
        name: '',
        activeTab: ETabs.SETTINGS,
        assignmentCount: 0,
        assignmentRefreshKey: 0,
        categoryId: 0,
        createdBy: '',
        createdDate: '',
        editable: false,
        editingName: false,
        error: null as string,
        hasApplicationQuestions: false,
        hasResults: false,
        isActive: true,
        isOwner: true,
        languageId: 0,
        languages: [] as CustomTestLanguage[],
        loading: true,
        loadingQuestions: false,
        minScore: 70,
        minutes: 60,
        modeId: EMode.TESTING as EMode,
        nameSnapshot: '',
        nameValidationError: '',
        newName: '',
        owner: '',
        productIds: [] as number[],
        questionIds: '',
        questionOrderMode: 0,
        questionsError: false,
        questionsFetched: false,
        questionsXml: '',
        questionTree: { Products: [] } as QuestionTree,
        randomly: 0,
        saving: false,
        selectedQuestionIds: '',
        smsWarningDismissed: false,
        testId: 0,
        totalQuestions: 0,
        useRandomly: false
    });

    const { name, activeTab, assignmentCount, categoryId, createdBy, editable, editingName, error, hasApplicationQuestions, hasResults, isActive, isOwner,
        loading, loadingQuestions, minScore, minutes, modeId, nameSnapshot, nameValidationError, newName, productIds, questionsError, questionTree,
        randomly, saving, selectedQuestionIds, smsWarningDismissed, testId, useRandomly } = state;

    const { formatMessage } = useIntl();
    const { permissions } = useCustomTests();
    const { createToast } = useNotification();
    const { testId: routeTestId } = useParams();
    const navigate = useNavigate();

    // Filtered lookup URL forwarded by the search page (and hops through the wizard), so the
    // breadcrumb and onward navigations can restore the user's filters.
    const location = useLocation();
    const fromLookup = (location.state as { from?: string } | null)?.from;

    const assignWizardRef = useRef<AssignWizardHandle>(null);
    const dialogRef = useRef<DialogHandle>(null);

    const { confirmArchive, confirmRestore } = useCustomTestArchiveDialogs(dialogRef);

    const questionsCount = selectedQuestionIds.split(',').map(s => s.trim()).filter(Boolean).length;

    const setActiveTab = (activeTab: ETabs) => setState({ activeTab });

    const fetchTestData = async (id: number, showLoader = true) => {

        try {

            setState(showLoader ? { error: null, loading: true } : { error: null });

            const [test, languages] = await Promise.all([
                CustomTestApi.Get(id),
                LanguagesApi.GetAll()
            ]) as [CustomTestDetail, CustomTestLanguage[]];

            setState({
                name: test?.Name,
                assignmentCount: test?.AssignmentCount ?? 0,
                categoryId: test?.CategoryId,
                createdBy: test?.CreatedBy ?? '',
                createdDate: test?.DateCreate ?? '',
                editable: test?.Editable ?? false,
                hasApplicationQuestions: test?.HasApplicationQuestions ?? false,
                hasResults: test?.HasResults,
                isActive: test?.IsActive,
                isOwner: test?.IsOwner ?? false,
                languageId: test?.LanguageId ?? 0,
                languages,
                loading: false,
                minScore: test?.MinScore,
                minutes: test?.Minutes,
                modeId: test?.ModeId,
                owner: test?.Owner ?? '',
                productIds: test?.ProductIds ?? [],
                questionIds: test?.SelectedQuestionIds ?? '',
                questionOrderMode: test?.QuestionOrderMode ?? 0,

                // The question-tree inputs may have changed, so allow the Questions tab
                // to fetch again even when the last fetch returned zero products.
                questionsFetched: false,
                questionsXml: test?.QuestionsXml ?? '',
                randomly: test?.Randomly,
                selectedQuestionIds: test?.SelectedQuestionIds ?? '',
                totalQuestions: test?.NumQuestions,
                useRandomly: test?.UseRandomly
            });

        } catch (e: any) {

            setState({
                error: e?.message ?? (formatMessage({ id: 'failed_to_load_this_custom_test.5b0f7b19', defaultMessage: 'Failed to load this Custom Test.' })),
                loading: false
            });

        }

    };

    const fetchQuestions = async () => {

        if (!state?.productIds?.length) return;

        try {

            setState({ loadingQuestions: true, questionsError: false });

            const productIdsStr = state.productIds.join(',');

            // Fall back to the first available language when the test has no explicit question
            // language (legacy tests persisted before LanguageId existed load with 0). Passing 0
            // returns an empty tree, so the questions would never display.
            const resolvedLanguageId = state.languageId || state.languages?.[0]?.Id || 0;

            const result: QuestionTreeResult = await CustomTestApi.GetQuestions(
                productIdsStr,
                state.categoryId,
                resolvedLanguageId,

                // Bank-selection mode, not the delivery mode: HasApplicationQuestions
                // identifies which bank the test was authored from (1 = in-app, 2 = browser).
                state.hasApplicationQuestions ? 1 : 2,
                state.selectedQuestionIds
            );

            setState({
                loadingQuestions: false,
                questionsFetched: true,
                questionsXml: result.XmlString,
                questionTree: result
            });

        } catch (e: any) {

            setState({ loadingQuestions: false, questionsError: true });

        }

    };

    const retryQuestions = () => {

        setState({ questionsFetched: false });
        fetchQuestions();

    };

    const handleQuestionsChange = (questionIds: string, totalSelected: number) => {

        setState({ questionIds, totalQuestions: totalSelected });

    };

    // Bumps a shared key that assignment-aware tabs watch, so assigning from the header or
    // any tab re-fetches the currently visible tab's data. The test detail is silently
    // re-fetched too so the header count and lock state update in-session.
    const refreshAssignments = () => {

        fetchTestData(testId, false);
        setState(prev => ({ assignmentRefreshKey: prev.assignmentRefreshKey + 1 }));

    };

    useEffect(() => {

        const params = new URLSearchParams(window.location.search);
        const testId = parseInt(routeTestId ?? params.get('testId') ?? '0', 10);

        if (testId > 0) {

            setState({ testId });
            fetchTestData(testId);

        } else {

            setState({ loading: false });

        }

    }, [routeTestId]); // eslint-disable-line react-hooks/exhaustive-deps

    // questionsFetched guards against an endless refetch loop when the server
    // legitimately returns zero products (each fetch stores a new empty tree object).
    useEffect(() => {

        if (state?.activeTab !== ETabs.QUESTIONS || state?.questionsError || state?.questionsFetched || state?.questionTree?.Products?.length) return;

        fetchQuestions();

    }, [state?.activeTab, state?.questionTree]);

    // Mirrors the lookup's copy-confirm dialog (CustomTestsSearch.handleCopyConfirmation) so the
    // details surface and the Questions-tab empty state confirm before entering the copy flow.
    const confirmCopy = () => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'copy_and_edit.9b9cf664', defaultMessage: 'Copy and Edit' }),
                    onClick: () => navigate(`${CUSTOM_TESTS_V2_BASE}/copy/${testId}`, { state: { from: fromLookup } })
                })
            ],
            body: (
                <p className={'mb-0'}>
                    <FormattedMessage defaultMessage='A copy of "{name}" will be created that you own. You can edit the copy without affecting the original test.' id="a_copy_of_name_will_be_created_that_you.989f1ef1" values={{ name }} />
                </p>
            ),
            title: formatMessage({ id: 'copy_this_test.db81d881', defaultMessage: 'Copy this test?' })
        });

    };

    const context: CustomTestEditContextValue = {
        name,
        assignmentRefreshKey: state.assignmentRefreshKey,
        categoryId,
        confirmCopy,
        createdBy,
        createdDate: state.createdDate,
        editable,
        handleQuestionsChange,
        hasApplicationQuestions,
        hasResults,
        isActive,
        isOwner,
        languageId: state.languageId,
        languages: state.languages,
        loadingQuestions,
        minScore,
        minutes,
        modeId,
        nameValidationError,
        owner: state.owner,
        productIds,
        questionOrderMode: state.questionOrderMode,
        questionsError,
        questionTree,
        randomly,
        refreshAssignments,
        retryQuestions,
        selectedQuestionIds,
        setProviderState: setState,
        testId,
        totalAssignmentCount: state.assignmentCount,
        totalQuestions: state.totalQuestions,
        useRandomly
    };

    // Confirms before entering the edit flow (mock modal), then navigates to the build wizard.
    const handleEdit = () => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction({ variant: 'outline-secondary' }),
                dialog.Action({
                    label: formatMessage({ id: 'edit_test.179c3e12', defaultMessage: 'Edit Test' }),
                    onClick: () => navigate(`${CUSTOM_TESTS_V2_BASE}/build/${testId}`, { state: { from: fromLookup } }),
                    variant: 'primary'
                })
            ],
            body: (
                <div className={'d-flex align-items-start gap-2'}>
                    <i className={'fa-solid fa-circle-info text-primary mt-1'} />
                    <div>
                        <h5 className={'fw-bold mb-1'}>
                            <FormattedMessage defaultMessage="Edit this test?" id="edit_this_test.2b536a1b" />
                        </h5>
                        <p className={'mb-0'}>
                            <FormattedMessage defaultMessage="We’ll open the Edit flow where you can update details, settings, and questions. Your changes are saved when you finish." id="well_open_the_edit_flow_where_you_can_up.8558f151" />
                        </p>
                    </div>
                </div>
            ),
            title: formatMessage({ id: 'edit_test.179c3e12', defaultMessage: 'Edit Test' })
        });

    };

    const showArchiveConfirmation = () => confirmArchive({
        onSuccess: () => setState({ isActive: false }),
        successMessage: formatMessage({ id: 'test_archived.ff72faa2', defaultMessage: 'Test archived.' }),
        testId
    });

    const showRestoreConfirmation = () => confirmRestore({
        onSuccess: () => setState({ isActive: true }),
        successMessage: formatMessage({ id: 'test_restored.9ad3501a', defaultMessage: 'Test restored.' }),
        testId
    });

    const handleAssign = async () => {

        const assigned = await assignWizardRef.current?.show({ modeId, testId, testName: name });

        if (assigned) {

            createToast(formatMessage({ id: 'assignments_saved_successfully.22d8fe61', defaultMessage: 'Assignments saved successfully.' }), 'success', 5000);
            refreshAssignments();

        }

    };

    const closeRenameModal = () => setState({ editingName: false, nameValidationError: '', newName: '' });

    const trimmedNewName = newName.trim();
    const isSameName = Boolean(trimmedNewName) && trimmedNewName === nameSnapshot;
    const isValidNewName = Boolean(trimmedNewName) && !isSameName;

    const handleSaveName = async () => {

        const trimmedName = trimmedNewName;

        if (!trimmedName || trimmedName === nameSnapshot) return;

        try {

            setState({ nameValidationError: '', saving: true });

            const result = await CustomTestApi.Rename(testId, trimmedName);

            if (result?.Done) {

                setState({ name: trimmedName, editingName: false, nameValidationError: '', newName: '' });
                createToast(formatMessage({ id: 'test_renamed.c664d89b', defaultMessage: 'Test renamed.' }), 'success', 5000);

            } else {

                setState({ nameValidationError: result?.Message || (formatMessage({ id: 'failed_to_rename_test.219deb1a', defaultMessage: 'Failed to rename test.' })) });

            }

        } catch (e: any) {

            setState({ nameValidationError: e?.message ?? (formatMessage({ id: 'failed_to_rename_test.219deb1a', defaultMessage: 'Failed to rename test.' })) });

        } finally {

            setState({ saving: false });

        }

    };

    const isLocked = hasResults || assignmentCount > 0;

    const pageHeader = (
        <div className="d-flex flex-column gap-2 pb-3">
            <Breadcrumb className="mb-0">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: fromLookup ?? CUSTOM_TESTS_V2_BASE }}>
                    <FormattedMessage defaultMessage="Custom Tests" id="custom_tests.9e7494ea" />
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {name || (formatMessage({ id: 'custom_test.acde46e9', defaultMessage: 'Custom Test' }))}
                </Breadcrumb.Item>
            </Breadcrumb>

            <div className="custom-test-edit__header-main d-flex align-items-center justify-content-between gap-3">
                <div className="custom-test-edit__title-wrap d-flex align-items-center gap-2">
                    <TruncatedText as="h1" className="m-0 fw-bold custom-test-edit__title" maxWidth="100%">
                        {name || (formatMessage({ id: 'custom_test.acde46e9', defaultMessage: 'Custom Test' }))}
                    </TruncatedText>
                    {!isActive && !loading && (
                        <Badge bg="warning" className="text-white">
                            <FormattedMessage defaultMessage="Archived" id="archived.e57c14b9" />
                        </Badge>
                    )}
                    {isActive && isOwner && permissions.CanEdit && !loading && (
                        <button
                            className="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center gap-1"
                            onClick={() => setState({ editingName: true, nameSnapshot: name, nameValidationError: '', newName: '' })}
                            type="button"
                        >
                            <i className="fa-solid fa-pen" />
                            <FormattedMessage defaultMessage="Edit Name" id="edit_name.d6eb60ef" />
                        </button>
                    )}
                </div>

                <div className="custom-test-edit__header-actions d-flex align-items-center gap-2">
                    {isOwner && (isActive ?
                        (
                            <GMetrixButton disabled={loading || !!error} iconClass="fa-regular fa-box-archive" onClick={showArchiveConfirmation} variant="outline-primary">
                                <FormattedMessage defaultMessage="Archive" id="archive.61ce1f3a" />
                            </GMetrixButton>
                        )
                        :
                        (
                            <GMetrixButton disabled={loading || !!error} iconClass="fa-regular fa-arrow-rotate-left" onClick={showRestoreConfirmation} variant="outline-primary">
                                <FormattedMessage defaultMessage="Restore" id="restore.d528bbad" />
                            </GMetrixButton>
                        ))}
                    <GMetrixButton disabled={loading || !!error} iconClass="fa-regular fa-copy" onClick={confirmCopy} variant="outline-primary">
                        <FormattedMessage defaultMessage="Copy" id="copy.fa37e351" />
                    </GMetrixButton>
                    {isActive && isOwner && permissions.CanAssign && (
                        <GMetrixButton
                            disabled={loading || !!error}
                            iconClass="fa-solid fa-user-plus"
                            onClick={handleAssign}
                            variant={hasResults ? 'primary' : 'outline-primary'}
                        >
                            <FormattedMessage defaultMessage="Assign" id="assign.0dabb6f2" />
                        </GMetrixButton>
                    )}
                    {editable && permissions.CanEdit && (
                        <GMetrixButton disabled={loading || !!error} iconClass="fa-solid fa-pen" onClick={handleEdit} variant="primary">
                            <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                        </GMetrixButton>
                    )}
                </div>
            </div>

            {!loading && !error && !isOwner && (
                <div className="d-flex align-items-start gap-2 bg-body border rounded px-3 py-2">
                    <i className="fa-solid fa-circle-info text-primary mt-1" />
                    <div>
                        <div className="fw-bold">
                            <FormattedMessage defaultMessage="Another user’s test" id="another_users_test.5c4b40e0" />
                        </div>
                        <div className="text-muted small">
                            <FormattedMessage defaultMessage="This test was created by {name}. Copy it to create an editable version of your own." id="this_test_was_created_by_name_copy_it_to.9a9e507b" values={{ name: createdBy || state.owner }} />
                        </div>
                    </div>
                </div>
            )}

            {!loading && !error && isOwner && isLocked && (
                <div className="d-flex align-items-start gap-2 bg-body border rounded px-3 py-2">
                    <i className="fa-solid fa-circle-info text-primary mt-1" />
                    <div>
                        <div className="fw-bold">
                            <FormattedMessage defaultMessage="Test Content Edits Unavailable" id="test_content_edits_unavailable.f919e718" />
                        </div>
                        <div className="text-muted small">
                            <FormattedMessage defaultMessage="This test has been assigned, so its questions and settings are locked. Copy the test to make changes." id="this_test_has_been_assigned_so_its_quest.9f5600c3" />
                        </div>
                    </div>
                </div>
            )}

            {!loading && !error && hasApplicationQuestions && !smsWarningDismissed && (
                <Alert
                    dismissible
                    className="mb-0"
                    onClose={() => setState({ smsWarningDismissed: true })}
                    variant="warning"
                >
                    <div className="d-flex align-items-start gap-2">
                        <i className="fa-solid fa-triangle-exclamation text-warning mt-1" />
                        <div>
                            <div className="fw-bold">
                                <FormattedMessage defaultMessage="SMS required" id="sms_required.e1197bd8" />
                            </div>
                            <div className="small">
                                <FormattedMessage defaultMessage="This test includes Application questions. Students will need SMS to complete them." id="this_test_includes_application_questions.e4379ccd" />
                            </div>
                        </div>
                    </div>
                </Alert>
            )}
        </div>
    );

    return (
        <CustomTestEditContext.Provider value={context} >
            <div className="custom-test-edit d-flex flex-column gap-3">

                {loading ?
                    (
                        <>
                            {pageHeader}
                            <div className="d-flex align-items-center justify-content-center p-5">
                                <GMetrixLoader />
                            </div>
                        </>
                    )
                    : error ?
                        (
                            <>
                                {pageHeader}
                                <div className="d-flex flex-column align-items-center text-center py-5">
                                    <i className="fa-regular fa-circle-exclamation fs-1 text-danger mb-3" />
                                    <h5 className="fw-bold mb-3">
                                        <FormattedMessage defaultMessage="Failed to load this Custom Test." id="failed_to_load_this_custom_test.5b0f7b19" />
                                    </h5>
                                    <GMetrixButton
                                        iconClass="fa-solid fa-arrow-rotate-right"
                                        onClick={() => fetchTestData(testId)}
                                        variant="primary"
                                    >
                                        <FormattedMessage defaultMessage="Retry" id="retry.225ca180" />
                                    </GMetrixButton>
                                </div>
                            </>
                        )
                        : testId === 0 ?
                            (
                                <>
                                    {pageHeader}
                                    <div className="alert alert-warning">

                                        <FormattedMessage defaultMessage="No test selected. Please return to the list." id="no_test_selected_please_return_to_the_li.7b160c58" />

                                        <div className="mt-2">
                                            <Link className={'btn btn-primary'} to={CUSTOM_TESTS_V2_BASE}>
                                                <FormattedMessage defaultMessage="Return to Custom Tests" id="return_to_custom_tests.f596c5cb" />
                                            </Link>
                                        </div>

                                    </div>
                                </>
                            )
                            :
                            (
                                <GMetrixTabs stickyHeader className="mb-3" header={pageHeader} onSelect={setActiveTab} selected={activeTab}>

                                    <GMetrixTabsTab title={formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' })}>
                                        <Tabs.Details />
                                    </GMetrixTabsTab>

                                    <GMetrixTabsTab
                                        title={(
                                            <span className="d-inline-flex align-items-center gap-2">
                                                <FormattedMessage defaultMessage="Questions" id="questions.940c8cce" />
                                                <Badge pill bg="dark">
                                                    {questionsCount}
                                                </Badge>
                                            </span>
                                        )}
                                    >
                                        <Tabs.Questions />
                                    </GMetrixTabsTab>

                                    <GMetrixTabsTab
                                        title={(
                                            <span className="d-inline-flex align-items-center gap-2">
                                                <FormattedMessage defaultMessage="Assignments" id="assignments.f2dfa1bf" />
                                                <Badge pill bg="dark">
                                                    {assignmentCount}
                                                </Badge>
                                            </span>
                                        )}
                                    >
                                        <Tabs.Assignments />
                                    </GMetrixTabsTab>

                                </GMetrixTabs>
                            )}

                <AssignWizard ref={assignWizardRef} />

                <Dialog ref={dialogRef} />

                <Modal centered onHide={closeRenameModal} show={editingName}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FormattedMessage defaultMessage="Edit Name" id="edit_name.d6eb60ef" />
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className={'mb-3'}>
                            <label className={'form-label'} htmlFor={'custom-test-rename-current'}>
                                <FormattedMessage defaultMessage="Current Name" id="current_name.c165b81f" />
                            </label>
                            <input
                                disabled
                                className={'form-control'}
                                id={'custom-test-rename-current'}
                                value={nameSnapshot}
                            />
                        </div>
                        <div>
                            <label className={'form-label'} htmlFor={'custom-test-rename-new'}>
                                <FormattedMessage defaultMessage="New Name" id="new_name.423f797c" />
                            </label>
                            <div className={'position-relative'}>
                                <input
                                    autoFocus
                                    className={'form-control pe-5'}
                                    id={'custom-test-rename-new'}
                                    onChange={e => setState({ nameValidationError: '', newName: e.target.value })}
                                    onKeyDown={e => e.key === 'Enter' && isValidNewName && !saving && handleSaveName()}
                                    placeholder={formatMessage({ id: 'enter_new_name.d6d27582', defaultMessage: 'Enter new name' })}
                                    value={newName}
                                />
                                {!!trimmedNewName && (
                                    <i
                                        className={`position-absolute top-50 translate-middle-y ${isValidNewName ? 'fa-solid fa-circle-check text-success' : 'fa-solid fa-circle-xmark text-danger'}`}
                                        style={{ right: '0.75rem' }}
                                    />
                                )}
                            </div>
                            {isSameName && (
                                <div className={'small text-danger mt-1'}>
                                    <FormattedMessage defaultMessage="This is already the name of this test. Enter a new name to save." id="this_is_already_the_name_of_this_test_en.a344a512" />
                                </div>
                            )}
                            {nameValidationError && (
                                <div className={'small text-danger mt-1'}>
                                    {nameValidationError}
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <GMetrixButton onClick={closeRenameModal} variant={'outline-secondary'}>
                            <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                        </GMetrixButton>
                        <GMetrixButton
                            disabled={!isValidNewName || saving}
                            onClick={handleSaveName}
                            variant={'primary'}
                        >
                            <FormattedMessage defaultMessage="Save Name" id="save_name.d15385ee" />
                        </GMetrixButton>
                    </Modal.Footer>
                </Modal>

            </div>
        </CustomTestEditContext.Provider>
    );

};
